/**
 * FeatureChip — the small status pill on the login brand panel.
 *
 * [OCCASION-CARDS-1 Slice 2] Rebuilt from two divergent copies that had each drifted into looking
 * cramped: a 4×4px dot (small enough to read as a rendering artifact rather than an indicator),
 * `py-1` on 10px text, and `rounded-lg` — an 8px radius on a ~22px-tall element, which is a rounded
 * rectangle, not a pill. The desktop copy used different numbers again, so the two never matched.
 *
 * One component now, two sizes. What changed and why:
 *   - the dot is 7px with a soft halo, so it reads as a deliberate "live" indicator;
 *   - `rounded-full`, so a pill is actually a pill at any height;
 *   - real vertical padding and a slightly larger, letter-spaced label;
 *   - a touch more contrast on the surface and border, because at 10% white on a purple gradient the
 *     old chip edge was barely visible at all.
 *
 * ⚠️ THE LABEL IS ONE STRING, PASSED IN. It is deliberately not assembled from parts here: the wording
 * ("22 AI-Powered Features") is pending a truth-in-marketing decision, and swapping it must be a
 * one-line change to the `brand.features_chip` locale value — not an edit to this component.
 *
 * @exports FeatureChip (named) · default
 */
import type { ReactNode } from 'react';

export interface FeatureChipProps {
  /** The complete label. One string, by design — see the note above. */
  children: ReactNode;
  /** `mobile` is the compact hero chip; `desktop` the roomier left-panel one. */
  size?: 'mobile' | 'desktop';
  /** Indicator colour. Green on the school panel, indigo in owner mode. */
  dotColor?: string;
}

export function FeatureChip({ children, size = 'desktop', dotColor = '#86EFAC' }: FeatureChipProps): JSX.Element {
  const compact = size === 'mobile';
  return (
    <div
      className="inline-flex items-center rounded-full"
      /*
       * A STABLE HOOK, so nothing has to find this chip by reading its words.
       *
       * `login-polish-s2-probe` located the chip with /AI-Powered Features/i and went 7/20 the moment
       * the copy changed — while the chip itself was perfectly fine. A probe that identifies an element
       * by its wording contradicts this component's own promise that the label is a one-line swap, and
       * it turns every future copy edit into a false alarm. The probe now selects on this attribute and
       * checks the TEXT against the locale value, which is the thing actually worth asserting.
       */
      data-feature-chip=""
      style={{
        gap: compact ? 8 : 10,
        padding: compact ? '7px 14px' : '9px 18px',
        background: 'rgba(255,255,255,0.13)',
        border: '1px solid rgba(255,255,255,0.22)',
        backdropFilter: 'blur(2px)',
        maxWidth: '100%',
      }}
    >
      <span
        className="shrink-0 rounded-full"
        style={{
          width: 7,
          height: 7,
          background: dotColor,
          // The halo is what turns a dot into an indicator. Without it, 7px of flat colour on a
          // translucent pill still reads as a speck.
          boxShadow: `0 0 0 3px ${dotColor}33`,
        }}
      />
      <span
        style={{
          fontSize: compact ? 11.5 : 13,
          fontWeight: 600,
          letterSpacing: '0.01em',
          color: 'rgba(255,255,255,0.92)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {children}
      </span>
    </div>
  );
}

export default FeatureChip;
